/**
 * Serviço Singleton para gerenciar a instância do DailyIframe
 * Garante que apenas uma instância do DailyIframe seja criada
 */
import DailyIframe from '@daily-co/daily-js';

class DailySingletonService {
  private static instance: DailySingletonService;
  private callObject: any = null;
  private isInitializing: boolean = false;
  private initPromise: Promise<any> | null = null;
  private eventListeners: Map<string, Set<Function>> = new Map();

  private constructor() {
    // Construtor privado para garantir o padrão Singleton
  }

  /**
   * Obtém a instância única do serviço
   */
  public static getInstance(): DailySingletonService {
    if (!DailySingletonService.instance) {
      DailySingletonService.instance = new DailySingletonService();
    }
    return DailySingletonService.instance;
  }

  /**
   * Inicializa o objeto de chamada com as opções fornecidas
   * @param options Opções para criar o objeto de chamada
   * @returns Uma promessa que resolve para o objeto de chamada
   */
  public async initialize(options: any): Promise<any> {
    // Se já estiver inicializando, retornar a promessa existente
    if (this.isInitializing && this.initPromise) {
      return this.initPromise;
    }

    // Se já tiver um objeto de chamada, retorná-lo
    if (this.callObject) {
      return Promise.resolve(this.callObject);
    }

    // Marcar como inicializando
    this.isInitializing = true;

    // Criar uma nova promessa de inicialização
    this.initPromise = new Promise(async (resolve, reject) => {
      try {
        console.log('[DailySingletonService] Inicializando Daily.co...');

        // Verificar se o Daily.co está disponível globalmente
        if (typeof window === 'undefined' || !window.DailyIframe) {
          // Tentar carregar o Daily.co se não estiver disponível
          if (window && window.loadDailyIfNeeded) {
            console.log('[DailySingletonService] Tentando carregar Daily.co...');
            try {
              await window.loadDailyIfNeeded();
              console.log('[DailySingletonService] Daily.co carregado com sucesso');
            } catch (loadError) {
              console.error('[DailySingletonService] Erro ao carregar Daily.co:', loadError);
              this.isInitializing = false;
              this.initPromise = null;
              reject(loadError);
              return;
            }
          } else {
            const error = new Error('Daily.co não está disponível globalmente');
            console.error('[DailySingletonService]', error);
            this.isInitializing = false;
            this.initPromise = null;
            reject(error);
            return;
          }
        }

        // Verificar novamente se o Daily.co está disponível
        if (!window.DailyIframe) {
          const error = new Error('Daily.co não está disponível após tentativa de carregamento');
          console.error('[DailySingletonService]', error);
          this.isInitializing = false;
          this.initPromise = null;
          reject(error);
          return;
        }

        // Criar o objeto de chamada
        console.log('[DailySingletonService] Criando objeto de chamada...');
        try {
          this.callObject = DailyIframe.createCallObject(options);
          console.log('[DailySingletonService] Objeto de chamada criado com sucesso');

          // Configurar eventos
          this.setupEventListeners();

          // Resolver a promessa com o objeto de chamada
          resolve(this.callObject);
        } catch (createError) {
          console.error('[DailySingletonService] Erro ao criar objeto de chamada:', createError);
          this.isInitializing = false;
          this.initPromise = null;
          reject(createError);
        }
      } catch (error) {
        console.error('[DailySingletonService] Erro ao inicializar Daily.co:', error);
        this.isInitializing = false;
        this.initPromise = null;
        reject(error);
      } finally {
        // Marcar como não inicializando
        this.isInitializing = false;
        this.initPromise = null;
      }
    });

    return this.initPromise;
  }

  /**
   * Configura os ouvintes de eventos para o objeto de chamada
   */
  private setupEventListeners(): void {
    if (!this.callObject) return;

    // Configurar eventos padrão
    const defaultEvents = [
      'meeting-state-updated',
      'participant-joined',
      'participant-left',
      'participant-updated',
      'error',
      'camera-error',
      'microphone-error',
      'joined-meeting',
      'left-meeting'
    ];

    // Adicionar ouvintes para eventos padrão
    defaultEvents.forEach(eventName => {
      this.callObject.on(eventName, (event: any) => {
        this.notifyEventListeners(eventName, event);
      });
    });
  }

  /**
   * Adiciona um ouvinte para um evento
   * @param eventName Nome do evento
   * @param callback Função de callback
   * @returns Uma função para remover o ouvinte
   */
  public addEventListener(eventName: string, callback: Function): () => void {
    if (!this.eventListeners.has(eventName)) {
      this.eventListeners.set(eventName, new Set());
    }

    const listeners = this.eventListeners.get(eventName);
    if (listeners) {
      listeners.add(callback);
    }

    // Retornar uma função para remover o ouvinte
    return () => {
      const listeners = this.eventListeners.get(eventName);
      if (listeners) {
        listeners.delete(callback);
      }
    };
  }

  /**
   * Notifica todos os ouvintes de um evento
   * @param eventName Nome do evento
   * @param event Dados do evento
   */
  private notifyEventListeners(eventName: string, event: any): void {
    const listeners = this.eventListeners.get(eventName);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(event);
        } catch (error) {
          console.error(`[DailySingletonService] Erro ao chamar ouvinte para evento ${eventName}:`, error);
        }
      });
    }
  }

  /**
   * Obtém o objeto de chamada
   */
  public getCallObject(): any {
    return this.callObject;
  }

  /**
   * Destrói o objeto de chamada
   */
  public destroy(): void {
    if (this.callObject) {
      console.log('[DailySingletonService] Destruindo objeto de chamada...');
      try {
        this.callObject.destroy();
        this.callObject = null;
        this.eventListeners.clear();
      } catch (error) {
        console.error('[DailySingletonService] Erro ao destruir objeto de chamada:', error);
      }
    }
  }
}

export default DailySingletonService;
